import { Injectable } from '@angular/core';
import { Observable, of, catchError, map } from 'rxjs';
import { IndustryTypeLookup } from 'src/app/model/ai-agent';
import { normalizeIndustryTypeLookups, sortIndustryTypeLookupsById } from 'src/app/modules/ai-studio/agentix-create/utils/industry-type.util';
import { OnboardingService } from 'src/app/services/onboarding/onboarding.service';

/** Profile step values submitted after email OTP verification. */
export interface AgentixTrialProfilePayload {
  email: string;
  name: string;
  organization: string;
  industryId: number;
  country: string;
  /** Country dial code followed by the phone number, e.g. `+971501234567`. */
  phone: string;
}

/** Result of email OTP verification. */
export interface AgentixTrialOtpVerificationResult {
  isValid: boolean;
  error?: string;
}

/** Country row for the profile step dropdown. */
export interface AgentixTrialCountryOption {
  id: string | number;
  name: string;
  phoneCountryCode: string;
  /** Mobile number validation pattern for this country, when configured. */
  regExpression?: string;
  /** Example mobile number shown as the input placeholder, when configured. */
  mobileNumberFormat?: string;
  /** Country flag image URL, when configured. */
  urlFlag?: string;
}

const PRODUCT_NAME = 'Addenda Agentix';

/** Industry labels hidden from the trial signup profile dropdown. */
const SIGNUP_EXCLUDED_INDUSTRY_NAMES = new Set([
  'education',
  'transportation',
  'real estate'
]);

/**
 * API-backed flow for Agentix trial signup: email OTP, profile capture, and auto-approval registration.
 */
@Injectable({ providedIn: 'root' })
export class AgentixTrialSignupFlowService {

  /**
   * @param onboardingService guest onboarding and self-registration APIs
   */
  constructor(
    private readonly onboardingService: OnboardingService
  ) {}

  /**
   * Ensures a guest OAuth token is available for public signup API calls.
   * @returns true when a token is stored locally
   */
  bootstrapGuestToken(): Observable<boolean> {
    if (localStorage.getItem('externalToken')) {
      return of(true);
    }

    return this.onboardingService.getToken().pipe(
      map((response: { access_token?: string }) => {
        if (response?.access_token) {
          localStorage.setItem('externalToken', response.access_token);
          return true;
        }
        localStorage.removeItem('externalToken');
        return false;
      }),
      catchError(() => {
        localStorage.removeItem('externalToken');
        return of(false);
      })
    );
  }

  /**
   * Loads industry types from guest GET /api/Lookup/type?type=IndustryType&isEnum=false.
   * @returns normalized industry lookup rows
   */
  loadIndustryTypes(): Observable<IndustryTypeLookup[]> {
    return this.onboardingService.getIndustryTypeList().pipe(
      map((response: { data?: unknown[] }) =>
        sortIndustryTypeLookupsById(normalizeIndustryTypeLookups(response?.data))
          .filter(row => !SIGNUP_EXCLUDED_INDUSTRY_NAMES.has(row.name.trim().toLowerCase()))
      ),
      catchError(() => of([]))
    );
  }

  /**
   * Loads countries for the profile step dropdown.
   * @returns country id/name/dial-code rows
   */
  loadCountries(): Observable<AgentixTrialCountryOption[]> {
    return this.onboardingService.getCountryODataList().pipe(
      map((countries: { value?: Array<Record<string, unknown>> }) => {
        const list = countries?.value?.map(item => ({
          id: item['Id'] as string | number,
          name: String(item['Name'] ?? ''),
          phoneCountryCode: String(item['PhoneCountryCode'] ?? '').trim(),
          regExpression: (item['RegExpression'] as string) || undefined,
          mobileNumberFormat: (item['MobileNumberFormat'] as string) || undefined,
          urlFlag: (item['UrlFlag'] as string) || undefined
        }));
        return Array.isArray(list) ? list.filter(row => row.name) : [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Sends the initial email-only auto-approval request to trigger an OTP.
   * @param email user email address
   * @returns true when the API reports success
   */
  sendCode(email: string): Observable<boolean> {
    return this.requestInitialStepOtp(email);
  }

  /**
   * Resends the email OTP using the same initial-step auto-approval request.
   * @param email user email address
   * @returns true when the API reports success
   */
  resendCode(email: string): Observable<boolean> {
    return this.requestInitialStepOtp(email);
  }

  /**
   * Triggers or re-triggers the email OTP via auto-approval with initialStep header.
   * @param email user email address
   * @returns true when the API reports success
   */
  private requestInitialStepOtp(email: string): Observable<boolean> {
    const normalizedEmail = email.trim().toLowerCase();
    return this.onboardingService
      .autoApprovalSelfRegistration(
        { email: normalizedEmail, selfRegistrationType: 2 },
        { initialStep: true }
      )
      .pipe(
        map((response: { success?: boolean }) => !!response?.success),
        catchError(() => of(false))
      );
  }

  /**
   * Validates the email OTP via the self-registration validate-otp endpoint.
   * @param email user email address
   * @param otp verification code entered by the user
   * @returns validation outcome and optional API error message
   */
  verifyCode(email: string, otp: string): Observable<AgentixTrialOtpVerificationResult> {
    const payload = {
      email: email.trim().toLowerCase(),
      phoneNumber: '',
      code: otp,
      isEmail: true,
      productName: PRODUCT_NAME
    };

    return this.onboardingService.validateSelfRegistrationOtp(payload).pipe(
      map((response: {
        verificationResult?: { isValid?: boolean; errors?: string[] };
      }) => ({
        isValid: !!response?.verificationResult?.isValid,
        error: response?.verificationResult?.errors?.[0]
      })),
      catchError(() => of({ isValid: false }))
    );
  }

  /**
   * Submits the completed profile via the auto-approval registration endpoint.
   * @param payload profile form values with verified email
   * @returns true when the API reports success
   */
  saveProfile(payload: AgentixTrialProfilePayload): Observable<boolean> {
    const registrationPayload = {
      email: payload.email.trim().toLowerCase(),
      name: payload.name,
      businessName: payload.organization,
      industryId: payload.industryId,
      country: payload.country,
      phone: payload.phone,
      selfRegistrationType: 2,
      isB2CRequired: true,
      productName: PRODUCT_NAME
    };

    return this.onboardingService.autoApprovalSelfRegistration(registrationPayload).pipe(
      map((response: { success?: boolean }) => !!response?.success),
      catchError(() => of(false))
    );
  }
}
